import moment from "moment";
import { useContext, useEffect, useState } from "react";

import classNames from "classnames";
import isEmpty from "lodash/fp/isEmpty";
import omit from "lodash/fp/omit";

import { Notification } from "@rio-cloud/rio-uikit";
import Button from "@rio-cloud/rio-uikit/Button";
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, { type SortDirectionType, } from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles, { type TableViewTogglesViewType, } from "@rio-cloud/rio-uikit/TableViewToggles";

import { InputDate, TableRowLoading } from "@components";
import { SocialUser, TripSummary } from "@types";
import { formatDateUTCBr } from "@utils";
import { AuthContext } from "src/providers/AuthProvider";
import { formatFloatToHours } from "src/utils/formatFloatToHours";
import Avatar from "../Avatar/indext";
import PaginatorSize, { ConfigPaginator } from "../PaginatorSize";
import TableChip from "../TableChip";

const defaultColumnOrder = [
  "travelDate",
  "name",
  "co2Emission",
  "fuelConsumption",
  "distanceTraveled",
  "timeTotal",
  "timeStops",
  "originDestination",
  "vehicle",
];

type ColumnLabel = {
  [key: string]: string;
};
const columnLabels: ColumnLabel = {
  travelDate: "Data da viagem",
  name: "Motorista",
  co2Emission: "Emissão de CO2",
  fuelConsumption: "Consumo combustível",
  distanceTraveled: "Distância percorrida",
  timeTotal: "Tempo total",
  timeStops: "Tempo paradas",
  originDestination: "Origem / Destino",
  vehicle: "Veículo",
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 270,
    defaultWidth: 270,
    maxWidth: 270,
  },
  // origin: {
  //   width: 200,
  //   defaultWidth: 200,
  //   maxWidth: 200,
  // },
  // destination: {
  //   width: 200,
  //   defaultWidth: 200,
  //   maxWidth: 200,
  // },
  // vehicle: {
  //   width: 260,
  //   defaultWidth: 260,
  //   maxWidth: 260,
  // },
};

const getSortDir = (
  sortDir: SortDirectionType,
  sortBy: string,
  previousSortBy: string
) => {
  if (sortBy === previousSortBy) {
    return sortDir === SortDirection.ASCENDING
      ? SortDirection.DESCENDING
      : SortDirection.ASCENDING;
  }
  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  size: number;
  totalSize: number;
  trips: TripSummary[];
  searchValue: string;
  followers?: Array<SocialUser>;
  usersAmity?: Amity.User[];
  pageSize: number;
  handleSearchValueChange: (text: string) => void;
  handleShowTripDetail: (driverId: string, tripId: string) => void;
  handleDownload: () => void;
  handlePage: (config: ConfigPaginator) => void;
  handleResetPage?: () => void;
  handleSort: (dir: string, newSort: string) => void;
  handleFilter?: () => void;
  onDateFilterChange?: (start?: string, end?: string) => void;
  isLoading: boolean;
};

const TablePerformance = (props: Props) => {
  const formatHours = (value: number | null): string => {
    if (value === undefined || value === null) return '0:00';
    return value.toFixed(2).toString().replace('.', ':');
  };

  const formatNumberToComma = (value: number | string | undefined | null): string => {
    if (value === undefined || value === null) return '0,00';

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return value?.toString() || '0,00';
    return numValue.toFixed(2).replace('.', ',');
  };

  const { viewType: externalViewType } = props;
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState<SortDirectionType>(
    SortDirection.ASCENDING
  );
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(
    externalViewType || TableViewToggles.VIEW_TYPE_TABLE
  );

  const [startDate, setStartDate] = useState<moment.Moment | undefined>(undefined);
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(undefined);

  useEffect(() => {
    if (startDate && endDate) {
      if (startDate.isAfter(endDate)) {
        Notification.error("Data inicial não pode ser maior do que a data final.");
        return;
      }
      const formattedStart = startDate.format("YYYY-MM-DD");
      const formattedEnd = endDate.format("YYYY-MM-DD");
      props.onDateFilterChange?.(formattedStart, formattedEnd);
    }

    if (!startDate && !endDate) {
      props.onDateFilterChange?.(undefined, undefined);
    }
  }, [startDate, endDate]);

  const handleToggleTableSettingsDialog = () =>
    setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) =>
    setViewType(newViewType);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  // For immediate effect
  const handleColumnDetailsChange = (
    column: string,
    newColumnDetails: ColumnDetails
  ) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      handleCardSortChange(newSortBy, getSortDir(sortDir, newSortBy, sortBy));
      props.handleSort(sortDir, newSortBy);
    }
  };

  const handleCardSortChange = (
    newSortBy: string,
    newSortDir: SortDirectionType
  ) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  };

  // May be extracted as a dedicated component but for demo purpose it's shown here
  const renderTableHead = (
    column: string,
    label: string,
    sortByColumn: string,
    sortDirection: SortDirectionType
  ) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? (
            <SortArrows direction={sortDirection} />
          ) : (
            <SortArrows />
          )}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  // filter for hidden columns
  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  // filter data to omit hidden columns
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = props.trips.map((trip) => ({
    ...withoutHidden(trip),
  })) as TripSummary[];

  // in case a search value is given, filter the data accordingly
  const searchResult = filteredRows;

  // Sort rows according to the sortBy and sortDir settings
  const rows = searchResult;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD &&
    "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS &&
    "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  const renderItems = (row?: TripSummary) => {
    const items = [
      {
        value: (
          <>
            <span className="rioglyph rioglyph-map-location margin-right-10 text-color-primary" />
            <span>Detalhes da viagem</span>
          </>
        ),
        onSelect: () => {
          const { driverId, tripId } = row ?? {};
          if (driverId && tripId) {
            props.handleShowTripDetail(driverId, tripId);
          }
        },
      },
    ];
    return items;
  };

  const inputProps1: React.HTMLProps<HTMLInputElement> = {
    placeholder: 'Data Inicial',
    id: '1',
    style: { width: 140 }
  };

  const inputProps2: React.HTMLProps<HTMLInputElement> = {
    placeholder: 'Data Final',
    id: '2',
    style: { width: 140 }
  };

  if (!startDate) inputProps1.value = '';
  if (!endDate) inputProps2.value = '';

  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const { getDetailUser } = useContext(AuthContext);

  return (
    <div id="table-trips">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column margin-bottom-0">
              <div className="btn-toolbar table-btn-toolbar">
                <div className="width-400">
                  <TableSearch
                    className="width-100pct"
                    value={props.searchValue}
                    onChange={props.handleSearchValueChange}
                    placeholder="Buscar por motorista, origem/destino e  veículo"
                  />
                </div>
                <div className="gap-10 display-flex " style={{ marginTop: -20 }}>
                  <InputDate
                    label=""
                    inputProps={inputProps1}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    value={startDate}
                    onChange={(value, isValid) => {
                      if (!value || value === "") {
                        setStartDate(undefined);
                      } else if (isValid && moment.isMoment(value)) {
                        setStartDate(value);
                      }
                    }}
                  />
                  <InputDate
                    label=""
                    inputProps={inputProps2}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    value={endDate || ''}
                    onChange={(value, isValid) => {
                      if (!value || value === "") {
                        setEndDate(undefined);
                      } else if (isValid && moment.isMoment(value)) {
                        setEndDate(value);
                      }
                    }}
                  />
                  <Button onClick={handleReset} bsStyle={Button.PRIMARY} style={{ marginTop: 20 }}
                    className="rioglyph rioglyph-trash"></Button>
                </div>
              </div>
            </div>
          </div>
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <TableViewToggles
                initialViewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent="Table view"
                singleCardViewTooltipContent="List view"
                multiCardsViewTooltipContent="Cards view"
              />
            </div>
          </div>
          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title="Table settings"
              onHide={handleToggleTableSettingsDialog}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={defaultColumnOrder}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={columnLabels}
              disabledColumns={[]}
              closeButtonText="Close"
              resetButtonText="Reset to default"
              searchPlaceholder="Search by column name"
              notFoundMessage="No column found for this search value"
              columnsDetails={columnsDetails}
              autoLabel="Auto"
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={(val: string) => console.log(val)}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>
      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir}
          onSortChange={handleCardSortChange}
        />
      )}
      <div>
        {isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState
            headline="Sem resultados"
            message="Por favor refaça a busca."
          />
        )}
        <table className={tableClassNames}>
          <colgroup>
            {columns.map((column) =>
              renderTableCaption(column, columnsDetails[column])
            )}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) =>
                renderTableHead(column, columnLabels[column], sortBy, sortDir)
              )}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
            {props.isLoading ? (
              <TableRowLoading cols={10} rows={10} />
            ) : null}

            {!props.isLoading && isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Sem resultados"
                    message="Por favor refaça a busca."
                  />
                </td>
              </tr>
            )}

            {!props.isLoading && rows.map((row: TripSummary, index: number) => {
              const user_amity = getDetailUser?.(row.profileId);
              return (
                <tr key={index}>
                  <td key={"Data da viagem"} data-field={"Data da viagem"}>
                    <span className="rioglyph rioglyph-calendar margin-right-10 text-color-darker text-size-16"></span>
                    <span>{formatDateUTCBr(row?.tripDate, false)}</span>
                  </td>
                  <td key={"Motorista"} data-field={"Motorista"}>
                    <div className="display-flex flex-direction-row align-items-center">
                      <Avatar
                        url={user_amity?.metadata?.imageProfile?.fileUrl}
                      />
                      <div className="padding-x-15">
                        <span className="text-bold text-size-16 text-capitalize">
                          {row.driverName}
                        </span>
                        <div className="margin-top-5">
                          <TableChip
                            disabledDriver={!row.driverId}
                            disabledProfile={!row.profileId}
                            user={user_amity}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td key="CO2" data-field="CO2">
                    <div className="display-flex align-items-center">
                      <span className="rioglyph rioglyph-globe margin-right-10 text-color-primary text-size-20"></span>
                      <div className="display-flex flex-column">
                        <span className="text-color-gray text-decoration-line-through">{formatNumberToComma(row.plannedCo2Emission)} kg</span>
                        <div className="">
                          <span className={`rioglyph rioglyph-sphere ${(row.co2Emission ?? 0) > (row.plannedCo2Emission ?? 0) ? 'text-color-danger' : 'text-color-success'} margin-right-2`}></span>
                          <span>{formatNumberToComma(row.co2Emission)} kg</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td key="Consumo" data-field="Consumo" >
                    <div className="display-flex align-items-center">
                      <span
                        className="rioglyph rioglyph-filling-station margin-right-10 text-color-primary text-size-20"></span>
                      <div className="display-flex flex-column">
                        <span className="text-color-gray text-decoration-line-through">{formatNumberToComma(row.plannedFuelConsumption)} litros</span>
                        <div className="">
                          <span className={`rioglyph rioglyph-sphere ${(row.fuelConsumption ?? 0) > (row.plannedFuelConsumption ?? 0) ? 'text-color-danger' : 'text-color-success'} margin-right-2`}></span>
                          <span>{formatNumberToComma(row.fuelConsumption)} litros</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td key={"Distãncia"} data-field={"Distãncia"}>
                    <div className="display-flex align-items-center">
                      <span className="rioglyph rioglyph-road margin-right-10 text-color-primary text-size-20"></span>
                      <div className="display-flex flex-column">
                        <span className="text-color-gray text-decoration-line-through">{formatNumberToComma(row.plannedDistance)} km</span>
                        <div className="">
                          <span className={`rioglyph rioglyph-sphere ${(row.distance || 0) <= (row.plannedDistance || 0) ? 'text-color-success' : 'text-color-danger'} margin-right-2`}></span>
                          <span>{formatNumberToComma(row.distance)} km</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td key="Tempo Total" data-field="Tempo Total">
                    <div className="display-flex align-items-center">
                      <span className="rioglyph rioglyph-time margin-right-10 text-color-primary text-size-20"></span>
                      <div className="display-flex flex-column">
                        <span className="text-color-gray text-decoration-line-through">{row.plannedTimeFormatted || "00h00m"}</span>
                        <div className="">
                          <span className={`rioglyph rioglyph-sphere ${(row.totalTime ?? 0) > (row.plannedTime ?? 0) ? 'text-color-danger' : 'text-color-success'} margin-right-2`}></span>
                          <span>{row.totalTimeFormatted ?? '00h00m'}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td key={"Tempo de Paradas"} data-field={"Tempo de Paradas"}>
                    <div className="display-flex align-items-center">
                      <span className="rioglyph rioglyph-pushpin margin-right-10 text-color-primary text-size-20"></span>
                      <div className="display-flex flex-column">
                        <span className="text-color-gray text-decoration-line-through">{formatFloatToHours(row.plannedIdleTime)}</span>
                        <div className="">
                          <span className={`rioglyph rioglyph-sphere ${(row.idleTime ?? 0) > (row.plannedIdleTime ?? 0) ? 'text-color-danger' : 'text-color-success'} margin-right-2`}></span>
                          <span>{row.idleTimeFormatted ?? '00h00m'}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td key={"Origem/Destino"} data-field={"Origem/Destino"}>
                    <span className="text-color-gray">{row.origin}</span>
                    <div className="">
                      <span className="rioglyph rioglyph-finish margin-right-10 text-color-darker"></span>
                      <span>{row.destination}</span>
                    </div>
                  </td>
                  <td key={"Veículo"} data-field={"Veículo"}>
                    <span>{row.vehicle}</span>
                  </td>
                  <td className="table-action">
                    <span>
                      <ButtonDropdown
                        bsSize="md"
                        dropdownClassName="width-200"
                        title={
                          <span className="rioglyph rioglyph-option-vertical" />
                        }
                        variant="link"
                        iconOnly
                        items={renderItems(row)}
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!props.isLoading ? (
        <PaginatorSize
          size={props.size}
          totalSize={props.totalSize}
          handleLoadMore={props.handlePage}
          handleReset={props.handleResetPage ?? (() => {})}
          isLoading={props.isLoading}
        />
      ) : null}
    </div>
  );
};

export default TablePerformance;
